import { useEffect } from "react";

/**
 * Controls where the keystroke belongs to whatever is being typed rather than to the page, so a
 * search box or a form field keeps the browser's own behaviour instead of driving the canvas.
 */
const isTextEntry = (target: EventTarget | null): boolean => {
	if (!(target instanceof HTMLElement)) {
		return false;
	}

	if (target.isContentEditable) {
		return true;
	}

	return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT";
};

/** True while a modal is on screen — every dialog in the app marks itself with `aria-modal`. */
const isDialogOpen = (): boolean => document.querySelector("[aria-modal='true']") !== null;

/**
 * Runs `onTrigger` when `key` is pressed with the platform's command modifier — Ctrl on Windows and
 * Linux, Cmd on macOS — and swallows whatever the browser would otherwise have done with it.
 *
 * Register it only where the command is actually offered: while `isEnabled` is false no listener
 * exists at all, so the browser keeps its default and the shortcut cannot fire from another page.
 *
 * The keystroke is left alone while focus sits in a text field or a dialog is open, since it
 * belongs to that context. Everywhere else it is swallowed even when `onTrigger` decides to do
 * nothing (a save already in flight, an empty undo history) — the browser's own handling should
 * not surface in those cases either. Adding Shift or Alt makes a different shortcut, which this
 * one never claims.
 */
export const useCommandShortcut = (key: string, onTrigger: () => void, isEnabled: boolean): void => {
	useEffect(() => {
		if (!isEnabled) {
			return undefined;
		}

		const onKeyDown = (event: KeyboardEvent): void => {
			const isCommandCombo = (event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey;
			if (!isCommandCombo || event.key.toLowerCase() !== key) {
				return;
			}

			if (isTextEntry(event.target) || isDialogOpen()) {
				return;
			}

			event.preventDefault();

			// A held key repeats; one action per press is enough.
			if (!event.repeat) {
				onTrigger();
			}
		};

		document.addEventListener("keydown", onKeyDown);

		return () => document.removeEventListener("keydown", onKeyDown);
	}, [isEnabled, key, onTrigger]);
};
